import StockUpdatedAt from '../components/StockUpdatedAt';
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsAPI } from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import toast from 'react-hot-toast';
import { useWmsStock } from '../hooks/useWmsStock';
import ProductSales from '../components/ProductSales';
import ProductImageGallery from '../components/ProductImageGallery';
import {
  ArrowLeft,
  Loader2,
  Save,
  Euro,
  Percent,
  Image,
} from 'lucide-react';

interface ProductFormData {
  codice: string;
  codice_metel: string;
  nome: string;
  descrizione: string;
  categoria: string;
  marca: string;
  unita_misura: string;
  prezzo_unitario: number;
  prezzo_acquisto?: number;
  aliquota_iva: number;
  is_servizio: boolean;
  is_attivo: boolean;
}

const initialFormData: ProductFormData = {
  codice: '',
  codice_metel: '',
  nome: '',
  descrizione: '',
  categoria: '',
  marca: '',
  unita_misura: 'pz',
  prezzo_unitario: 0,
  prezzo_acquisto: undefined,
  aliquota_iva: 22,
  is_servizio: false,
  is_attivo: true,
};

export default function ProductEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const isNew = !id || id === 'new';
  const canSave = isNew ? hasPermission('prodotti:create') : hasPermission('prodotti:edit');

  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get('returnTo');
  const returnTo = requestedReturnTo === '/products' || requestedReturnTo?.startsWith('/products?')
    ? requestedReturnTo : '/products';
  const canReadSales = !isNew && hasPermission('offerte:view');
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') === 'vendite' && canReadSales ? 'vendite' : 'dettagli');
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  // Fetch existing product
  const { data: product, isLoading: loadingProduct } = useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const res = await productsAPI.getProduct(id!);
      return res.data;
    },
    enabled: !isNew,
  });

  const canReadStock = !isNew && !!product && !formData.is_servizio && hasPermission('wms:view');
  const { data: stock, isLoading: loadingStock, isError: stockError } = useWmsStock(canReadStock ? id : undefined);

  // Fetch categories for datalist
  const { data: categories } = useQuery({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const res = await productsAPI.getCategories();
      return res.data;
    },
  });

  // Fetch brands for datalist
  const { data: brands } = useQuery({
    queryKey: ['product-brands'],
    queryFn: async () => {
      const res = await productsAPI.getBrands();
      return res.data;
    },
  });

  // Populate form when editing
  useEffect(() => {
    if (product) {
      setFormData({
        codice: product.codice || '',
        codice_metel: product.codice_metel || '',
        nome: product.nome,
        descrizione: product.descrizione || '',
        categoria: product.categoria || '',
        marca: product.marca || '',
        unita_misura: product.unita_misura,
        prezzo_unitario: product.prezzo_unitario,
        prezzo_acquisto: product.prezzo_acquisto,
        aliquota_iva: product.aliquota_iva,
        is_servizio: product.is_servizio,
        is_attivo: product.is_attivo,
      });
    }
  }, [product]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data: ProductFormData) => {
      const payload = {
        ...data,
        codice: data.codice || undefined,
        codice_metel: data.codice_metel.trim().toUpperCase() || undefined,
        descrizione: data.descrizione || undefined,
        categoria: data.categoria || undefined,
        marca: data.marca || undefined,
      };
      if (isNew) {
        return productsAPI.createProduct(payload);
      } else {
        return productsAPI.updateProduct(id!, payload);
      }
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['wms-stock'] });
      queryClient.invalidateQueries({ queryKey: ['wms-stock-batch'] });
      queryClient.invalidateQueries({ queryKey: ['product-categories'] });
      queryClient.invalidateQueries({ queryKey: ['product-brands'] });
      toast.success(isNew ? 'Prodotto creato con successo' : 'Prodotto aggiornato con successo');
      if (isNew) {
        navigate(`/products/${res.data.id}/edit?${new URLSearchParams({ returnTo })}`, { replace: true });
      } else {
        queryClient.invalidateQueries({ queryKey: ['product', id] });
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSave) saveMutation.mutate(formData);
  };

  const updateField = (field: keyof ProductFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (!isNew && loadingProduct) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  const margine =
    formData.prezzo_acquisto && formData.prezzo_acquisto > 0 && formData.prezzo_unitario > 0
      ? ((formData.prezzo_unitario - formData.prezzo_acquisto) / formData.prezzo_unitario) * 100
      : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => navigate(returnTo)} aria-label="Torna ai prodotti"
            className="p-2 rounded-lg hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-500">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">
            {isNew ? 'Nuovo prodotto' : 'Scheda prodotto'}
          </h1>
        </div>
        {canSave && (
          <button type="submit" form="product-form" disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isNew ? 'Crea prodotto' : 'Salva modifiche'}
          </button>
        )}
      </div>

      <form id="product-form" onSubmit={handleSubmit} className="space-y-5">
        <section aria-label="Identità del prodotto" className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(240px,320px)_1fr]">
            <section aria-label="Immagini del prodotto" className="p-5 bg-gray-50/70 border-b lg:border-b-0 lg:border-r border-gray-200">
              {(() => {
                const images = product?.immagini || [];
                const selected = images.find((img: any) => img.id === selectedImageId)
                  || images.find((img: any) => img.is_default) || images[0];
                return <>
                  <div className="h-48 sm:h-56 flex items-center justify-center rounded-lg bg-white overflow-hidden p-3">
                    {selected?.url ? (
                      <img src={selected.url} alt={selected.alt_text || formData.nome || 'Immagine del prodotto'}
                        className="h-auto w-auto max-h-full max-w-full object-contain" />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-gray-400">
                        <Image className="h-10 w-10" strokeWidth={1.25} />
                        <span className="text-sm">Nessuna immagine</span>
                      </div>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-1" aria-label="Seleziona immagine">
                      {images.map((img: any, index: number) => (
                        <button type="button" key={img.id} onClick={() => setSelectedImageId(img.id)}
                          aria-label={`Immagine ${index + 1}`} aria-pressed={selected?.id === img.id}
                          className={`h-12 w-12 shrink-0 rounded-md border bg-white overflow-hidden focus-visible:ring-2 focus-visible:ring-blue-500 ${selected?.id === img.id ? 'border-blue-500' : 'border-gray-200'}`}>
                          {img.url ? <img src={img.url} alt={img.alt_text || `Vista ${index + 1}`} className="h-auto w-auto max-h-full max-w-full object-contain m-auto p-1" /> : <Image className="h-5 w-5 m-auto text-gray-300" />}
                        </button>
                      ))}
                    </div>
                  )}
                </>;
              })()}
              {!isNew && canSave && (
                <button type="button" onClick={() => setShowImageGallery(true)}
                  className="mt-3 w-full inline-flex justify-center items-center gap-2 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500">
                  <Image className="h-4 w-4" /> Gestisci immagini
                </button>
              )}
              {isNew && <p className="mt-3 text-xs text-gray-500">Salva il prodotto per aggiungere le immagini.</p>}
            </section>

            <div className="p-5 sm:p-6 space-y-5 min-w-0">
              <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="product-code" className={labelClass}>Codice interno</label>
                <input id="product-code" value={formData.codice} readOnly={!canSave}
                  onChange={(e) => updateField('codice', e.target.value)}
                  className={`${inputClass} font-mono read-only:bg-gray-50`} />
              </div>
                <div className="min-w-0">
                  <label htmlFor="product-metel" className={labelClass}>Codice METEL</label>
                  <input id="product-metel" value={formData.codice_metel} readOnly={!canSave}
                    onChange={(e) => updateField('codice_metel', e.target.value)}
                    title="Usato per associare listini e immagini METEL"
                    className={`${inputClass} font-mono read-only:bg-gray-50`} />
                </div>
              </div>
              <div>
                <label htmlFor="product-name" className={labelClass}>Nome *</label>
                <input id="product-name" value={formData.nome} readOnly={!canSave} required
                  onChange={(e) => updateField('nome', e.target.value)}
                  className={`${inputClass} !text-lg font-semibold read-only:bg-gray-50`} />
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-4 border-t border-gray-100 pt-5">
                {[
                  { label: 'Attivo', checked: formData.is_attivo, field: 'is_attivo' as const, value: !formData.is_attivo },
                  { label: 'Giacenza', checked: !formData.is_servizio, field: 'is_servizio' as const, value: !formData.is_servizio },
                ].map(({ label, checked, field, value }) => (
                  <div key={field} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    <button type="button" role="switch" aria-label={label} aria-checked={checked} disabled={!canSave}
                      onClick={() => updateField(field, value)}
                      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}>
                      <span className={`h-4 w-4 rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-sm text-gray-500">{checked ? 'Sì' : 'No'}</span>
                  </div>
                ))}
              </div>

        {canReadStock && (
                <div aria-label="Riepilogo giacenza" className="border-t border-gray-100 pt-4 flex items-center justify-between gap-4 whitespace-nowrap overflow-x-auto">
                  <StockUpdatedAt stock={stock} />
                  {loadingStock ? <p className="text-sm text-gray-500">Caricamento giacenza…</p>
                    : stockError || !stock?.wms_available ? <p className="text-sm text-gray-500">Giacenza non disponibile</p>
                    : <p className="text-sm text-gray-500">Totale giacenza <strong className="ml-2 text-lg font-semibold text-gray-900 tabular-nums">{stock.total_quantity?.toLocaleString('it-IT')} <span className="text-sm">{product.unita_misura}</span></strong></p>}
                </div>
              )}
            <div>
              <label htmlFor="product-price" className={labelClass}>Prezzo *</label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  id="product-price"
                  disabled={!canSave}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.prezzo_unitario}
                  onChange={(e) =>
                    updateField('prezzo_unitario', parseFloat(e.target.value) || 0)
                  }
                  required
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          {product?.prezzo_listino_base != null && (
            <p className="text-sm text-gray-700">
              {product.listino_base_nome || 'Listino base'}:{' '}
              <strong>{Number(product.prezzo_listino_base).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })}</strong>
              {' · '}Per modificare questo prezzo, apri il listino.
            </p>
          )}

            </div>
          </div>
        </section>

              <div className="flex gap-1 border-b border-gray-200 overflow-x-auto" role="tablist" aria-label="Informazioni prodotto">
          {[
            { id: 'dettagli', label: 'Dettagli' },
            { id: 'costi', label: 'Costi' },
            { id: 'giacenze', label: 'Giacenze' },
            ...(canReadSales ? [{ id: 'vendite', label: 'Vendite' }] : []),
          ].map((tab, index, tabs) => (
            <button key={tab.id} type="button" role="tab" id={`tab-${tab.id}`} aria-controls={`panel-${tab.id}`}
              aria-selected={activeTab === tab.id} tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => setActiveTab(tab.id)}
              onKeyDown={(event) => {
                const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
                  : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
                  : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
                if (next !== null) {
                  event.preventDefault();
                  setActiveTab(tabs[next].id);
                  document.getElementById(`tab-${tabs[next].id}`)?.focus();
                }
              }}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        {canReadSales && <div role="tabpanel" id="panel-vendite" aria-labelledby="tab-vendite" hidden={activeTab !== 'vendite'}>
          {activeTab === 'vendite' && <ProductSales key={id} productId={id!} />}
        </div>}
        <div role="tabpanel" id="panel-giacenze" aria-labelledby="tab-giacenze" hidden={activeTab !== 'giacenze'}>
          {!canReadStock && <p className="bg-white rounded-xl border border-gray-200 p-6 text-sm text-gray-500">{formData.is_servizio ? 'Giacenza disattivata per questo prodotto.' : isNew ? 'Salva il prodotto per consultare le giacenze.' : 'Non hai i permessi per consultare le giacenze.'}</p>}

        {canReadStock && (
          <section className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6" aria-label="Giacenza di magazzino">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Disponibilità per magazzino</h2>
            {loadingStock ? <p className="text-sm text-gray-500">Caricamento giacenza…</p>
              : stockError || !stock?.wms_available ? (
                <p className="text-sm text-gray-500">Giacenza non disponibile. Verifica la connessione WMS e il codice prodotto.</p>
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="w-full table-fixed text-sm text-center tabular-nums">
                      <caption className="sr-only">Disponibilità per magazzino</caption>
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          {(stock.warehouses || []).map((warehouse) => <th key={warehouse.warehouse_id} scope="col" className="px-3 py-3 font-medium break-words">{warehouse.warehouse_name || warehouse.warehouse_id}</th>)}
                        </tr>
                      </thead>
                      <tbody><tr className="border-t border-gray-200">
                        {(stock.warehouses || []).map((warehouse) => <td key={warehouse.warehouse_id} className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{warehouse.available_quantity.toLocaleString('it-IT')} {product.unita_misura}</td>)}
                      </tr></tbody>
                    </table>
                  </div>
                </>
              )}
          </section>
        )}

        </div>
        <fieldset disabled={!canSave} className="disabled:opacity-75">
        <div role="tabpanel" id="panel-dettagli" aria-labelledby="tab-dettagli" hidden={activeTab !== 'dettagli'}>
        {/* Dettagli */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Dettagli</h2>


          {product?.scheda_metel_url && /^https?:\/\//i.test(product.scheda_metel_url) && (
            <div className="mb-5">
              <p className={labelClass}>Scheda METEL</p>
              <a href={product.scheda_metel_url} target="_blank" rel="noopener noreferrer"
                className="text-sm font-medium text-blue-600 hover:underline break-all">
                Apri scheda METEL ↗
              </a>
            </div>
          )}
          <div>
            <label htmlFor="product-description" className={labelClass}>Descrizione</label>
            <textarea id="product-description"
              value={formData.descrizione}
              onChange={(e) => updateField('descrizione', e.target.value)}
              rows={3}
              className={inputClass}
            />
          </div>
          <h3 className="text-sm font-semibold text-gray-900 border-t border-gray-100 pt-4">Classificazione</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="product-categoria" className={labelClass}>Categoria</label>
              <input id="product-categoria"
                type="text"
                value={formData.categoria}
                onChange={(e) => updateField('categoria', e.target.value)}
                list="categories"
                className={inputClass}
              />
              <datalist id="categories">
                {categories?.map((cat: string) => (
                  <option key={cat} value={cat} />
                ))}
              </datalist>
            </div>
            <div>
              <label htmlFor="product-marca" className={labelClass}>Marca</label>
              <input id="product-marca"
                type="text"
                value={formData.marca}
                onChange={(e) => updateField('marca', e.target.value)}
                list="brands"
                className={inputClass}
              />
              <datalist id="brands">
                {brands?.map((brand: string) => (
                  <option key={brand} value={brand} />
                ))}
              </datalist>
            </div>
            <div>
              <label htmlFor="product-unita_misura" className={labelClass}>Unita di Misura *</label>
              <select id="product-unita_misura"
                value={formData.unita_misura}
                onChange={(e) => updateField('unita_misura', e.target.value)}
                className={inputClass}
              >
                <option value="pz">Pezzi (pz)</option>
                <option value="kg">Chilogrammi (kg)</option>
                <option value="g">Grammi (g)</option>
                <option value="l">Litri (l)</option>
                <option value="ml">Millilitri (ml)</option>
                <option value="m">Metri (m)</option>
                <option value="cm">Centimetri (cm)</option>
                <option value="mq">Metri quadrati (mq)</option>
                <option value="h">Ore (h)</option>
                <option value="gg">Giorni (gg)</option>
              </select>
            </div>
          </div>
        </div>

        </div>
        <div role="tabpanel" id="panel-costi" aria-labelledby="tab-costi" hidden={activeTab !== 'costi'}>
        {/* Costi */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Costi</h2>


          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Prezzo Acquisto</label>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.prezzo_acquisto ?? ''}
                  onChange={(e) =>
                    updateField(
                      'prezzo_acquisto',
                      e.target.value ? parseFloat(e.target.value) : undefined
                    )
                  }
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
            <div>
              <label className={labelClass}>Aliquota IVA *</label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <select
                  value={formData.aliquota_iva}
                  onChange={(e) => updateField('aliquota_iva', parseFloat(e.target.value))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value={22}>22%</option>
                  <option value={10}>10%</option>
                  <option value={5}>5%</option>
                  <option value={4}>4%</option>
                  <option value={0}>0% (Esente)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Margine Preview */}
          {margine !== null && (
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Margine calcolato:</span>
                <span
                  className={`text-lg font-medium ${
                    margine >= 30
                      ? 'text-green-600'
                      : margine >= 15
                      ? 'text-yellow-600'
                      : 'text-red-600'
                  }`}
                >
                  {margine.toFixed(1)}%
                </span>
              </div>
            </div>
          )}
        </div>

        </div>
        </fieldset>
      </form>

      {/* Image Gallery Modal */}
      {showImageGallery && product && (
        <ProductImageGallery
          isOpen={true}
          onClose={() => {
            setShowImageGallery(false);
            queryClient.invalidateQueries({ queryKey: ['product', id] });
            queryClient.invalidateQueries({ queryKey: ['products'] });
          }}
          prodottoId={product.id}
          prodottoNome={product.nome}
        />
      )}
    </div>
  );
}
